import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { cn } from "../lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faTimes,
  faSignOutAlt,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";

interface MaterialLayoutProps {
  children: React.ReactNode;
}

const MaterialLayout: React.FC<MaterialLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const navItems = [
    { label: "Home", path: "/" },
    ...(isAuthenticated
      ? [
          { label: "Assessment", path: "/assessment" },
          { label: "History", path: "/history" },
          ...(isAdmin ? [{ label: "Data", path: "/data" }] : []),
        ]
      : []),
    { label: "About", path: "/about" },
    { label: "Contact", path: "/contact" },
  ];

  const handleLogout = () => {
    logout();
    navigate("/login");
    setMobileOpen(false);
  };

  const getUserInitials = () => {
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navigation Bar */}
      <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 md:px-6 sticky top-0 z-40 shadow-sm">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0 mr-8">
          <img
            src="/favicon-32x32.png?v=medical-icon"
            alt="Boobalyz"
            className="w-8 h-8 rounded-full object-contain"
          />
          <span className="text-lg font-bold text-slate-900 hidden sm:block">
            Boobalyz
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <nav className="hidden md:flex items-center gap-1 flex-1">
          {navItems.map((item) => (
            <Link
              key={item.label}
              to={item.path}
              className={cn(
                "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                location.pathname === item.path
                  ? "text-blue-600 bg-blue-50 font-semibold"
                  : "text-slate-600 hover:text-blue-600 hover:bg-slate-50",
              )}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right Side: Search + User */}
        <div className="flex items-center gap-3 ml-auto">
          {/* Search Bar */}
          <div className="hidden md:flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-slate-400 text-sm"
            />
            <input
              type="text"
              placeholder="Search anything..."
              className="bg-transparent text-sm text-slate-700 placeholder-slate-400 outline-none w-48"
            />
          </div>

          {/* User Avatar / Sign In */}
          {isAuthenticated ? (
            <div className="relative group">
              <button className="w-9 h-9 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center hover:bg-blue-700 transition-colors">
                {getUserInitials()}
              </button>
              {/* Dropdown on hover */}
              <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-xs text-slate-500 truncate">
                    {user?.email}
                  </p>
                  <p className="text-xs font-medium text-slate-700">
                    {user?.role === "administrator" ? "Admin" : "User"}
                  </p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <FontAwesomeIcon
                    icon={faSignOutAlt}
                    className="text-slate-400"
                  />
                  Sign out
                </button>
              </div>
            </div>
          ) : (
            <Link
              to="/login"
              className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Sign In
            </Link>
          )}

          {/* Mobile Hamburger */}
          <button
            className="md:hidden p-2 text-slate-600 hover:text-slate-900 transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <FontAwesomeIcon icon={mobileOpen ? faTimes : faBars} />
          </button>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
        >
          <div className="absolute inset-0 bg-black/20" />
          <div
            className="absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <nav className="flex flex-col p-4 gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "px-4 py-3 rounded-lg text-sm font-medium transition-colors",
                    location.pathname === item.path
                      ? "text-blue-600 bg-blue-50 font-semibold"
                      : "text-slate-700 hover:bg-slate-50",
                  )}
                >
                  {item.label}
                </Link>
              ))}
              {isAuthenticated && (
                <>
                  <div className="border-t border-slate-100 mt-2 pt-2">
                    <p className="px-4 py-1 text-xs text-slate-500">
                      {user?.email}
                    </p>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} />
                      Sign out
                    </button>
                  </div>
                </>
              )}
              {!isAuthenticated && (
                <Link
                  to="/login"
                  onClick={() => setMobileOpen(false)}
                  className="px-4 py-3 bg-blue-600 text-white text-center font-semibold rounded-lg mt-2"
                >
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1">{children}</main>
    </div>
  );
};

export default MaterialLayout;
